/**
 * Compute the override: fields in `current` that differ from `defaults`.
 * Returns null if no differences (meaning "use default").
 * Recurses into nested objects. Arrays are compared by JSON equality.
 */
export function computeOverride(
    current: Record<string, unknown>,
    defaults: Record<string, unknown>
): Record<string, unknown> | null {
    const diff: Record<string, unknown> = {};
    let hasDiff = false;

    for (const [key, currentVal] of Object.entries(current)) {
        const defaultVal = defaults[key];

        // Both are plain objects → recurse
        if (
            currentVal !== null &&
            defaultVal !== null &&
            typeof currentVal === 'object' &&
            typeof defaultVal === 'object' &&
            !Array.isArray(currentVal) &&
            !Array.isArray(defaultVal)
        ) {
            const nested = computeOverride(
                currentVal as Record<string, unknown>,
                defaultVal as Record<string, unknown>
            );
            if (nested !== null) {
                diff[key] = nested;
                hasDiff = true;
            }
            continue;
        }

        // Leaf comparison (JSON.stringify handles arrays and primitives)
        if (JSON.stringify(currentVal) !== JSON.stringify(defaultVal)) {
            diff[key] = currentVal;
            hasDiff = true;
        }
    }

    return hasDiff ? diff : null;
}
